/**
 * Derived selectors for the Dashboard. All take an effective current BTC price
 * (caller decides live vs manual) and run engine functions to produce display
 * values. Pure  -  no React, no store access.
 */

import { Decimal } from '../engine/money.js';
import {
  totalBtcBalance, stackSatoshis, stackUsdValue, totalUsdInvested,
  unrealizedGainLoss, unrealizedGainLossPercent, progressToGoal,
  estimatedDateToGoal, formatTimeToGoal,
  type PaceMethod,
} from '../engine/purchases.js';
import type { PurchaseTransaction, Goal } from '../engine/types.js';

export interface DashboardSnapshot {
  totalBtc: Decimal;
  totalSats: bigint;
  stackUsd: Decimal;
  totalInvested: Decimal;
  gainUsd: Decimal;
  gainPctCost: Decimal;
  gainPctValue: Decimal;
  goals: Array<{
    goal: Goal;
    progress: Decimal;
    eta: { date: Date | null; formatted: string };
  }>;
}

export function selectDashboard(args: {
  purchases: PurchaseTransaction[];
  goals: Goal[];
  currentBtcPriceUsd: Decimal;
  asOf: Date;
  paceMethod: PaceMethod;
}): DashboardSnapshot {
  const totalBtc = totalBtcBalance(args.purchases);
  const totalSats = stackSatoshis(args.purchases);
  const stackUsd = stackUsdValue(totalBtc, args.currentBtcPriceUsd);
  const totalInvested = totalUsdInvested(args.purchases);
  const gainUsd = unrealizedGainLoss(stackUsd, totalInvested);
  const gainPctCost = unrealizedGainLossPercent(gainUsd, totalInvested, stackUsd, 'cost');
  const gainPctValue = unrealizedGainLossPercent(gainUsd, totalInvested, stackUsd, 'value');
  const goals = args.goals.map(g => {
    const progress = progressToGoal(totalBtc, g.targetBtc);
    const eta = estimatedDateToGoal({
      txs: args.purchases,
      targetBtc: g.targetBtc,
      asOf: args.asOf,
      method: args.paceMethod,
    });
    return {
      goal: g,
      progress,
      eta: { date: eta, formatted: formatTimeToGoal(args.asOf, eta) },
    };
  });
  return { totalBtc, totalSats, stackUsd, totalInvested, gainUsd, gainPctCost, gainPctValue, goals };
}
